import { useBreakpoints } from '@assets/scripts/composables/useBreakpoints.js'

(function () {
  const isTouchDevice =
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0

  function init() {
    const elements = document.querySelectorAll('[data-collapse-root]')
    if (!elements.length) return

    elements.forEach((root) => {
      const initHandler = () => initCollapsible(root)

      const initRuleRaw = root.getAttribute('data-collapse-init') ?? null
      const rule = parseCollapseInit(initRuleRaw) ?? null
      const media = rule ? getMediaForRule(rule) : null

      // always inited
      if (!media) {
        initHandler()
        return
      }

      // inited by the media condition
      if (media.matches) initHandler()
      media.addEventListener('change', () => {
        if (media.matches) {
          initHandler()
        } else {
          resetCollapsible(root)
        }
      })
    })
  }

  function initCollapsible(root) {
    const trigger = root.querySelector('[data-collapse-trigger]')
    if (!trigger) return

    const targetId = trigger.dataset.collapseTarget
    if (!targetId) return

    const content = document.getElementById(targetId)
    if (!content) return

    if (trigger.dataset.collapseInited === 'true') return
    trigger.dataset.collapseInited = 'true'

    // ================================
    // Animation initial states
    // ================================
    content.style.overflow = content.style.overflow || 'hidden'
    content.style.transition = content.style.transition || 'height 0.5s ease'

    content.style.height = '0px'
    root.removeAttribute('data-collapse-expanded')

    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'height') return

      const expanded = trigger.getAttribute('aria-expanded') === 'true'
      if (expanded) {
        content.style.height = 'auto'
      }
    }

    if (!content.dataset.collapseTransitionBound) {
      content.addEventListener('transitionend', onTransitionEnd)
      content.dataset.collapseTransitionBound = 'true'
    }

    // Options
    let options = []
    let activeIndex = -1

    const refreshOptions = () => {
      options = Array.from(content.querySelectorAll('[role="option"]'))
      options.forEach((opt) => {
        opt.setAttribute('aria-selected', 'false')
        opt.setAttribute('tabindex', '0')
      })
      activeIndex = options.length ? 0 : -1
    }

    const focusOption = (idx) => {
      if (!options.length) return

      idx = (idx + options.length) % options.length

      options.forEach((opt, i) => {
        opt.setAttribute('aria-selected', i === idx ? 'true' : 'false')
      })

      options[idx].focus()
      activeIndex = idx
    }

    // ================================
    // Logic
    // ================================
    function expand(focusFirst = false) {
      refreshOptions()

      const startHeight = content.offsetHeight

      content.style.height = startHeight + 'px'
      void content.offsetHeight // reflow

      const targetHeight = content.scrollHeight
      content.style.height = targetHeight + 'px'

      trigger.setAttribute('aria-expanded', 'true')
      trigger.setAttribute('aria-label', trigger.dataset.collapseLabel ?? 'Collapse')
      root.setAttribute('data-collapse-expanded', 'true')

      if (focusFirst && options[0]) {
        focusOption(0)
      }
    }

    function collapse() {
      const startHeight = content.offsetHeight

      content.style.height = startHeight + 'px'
      void content.offsetHeight

      content.style.height = '0px'

      trigger.setAttribute('aria-expanded', 'false')
      trigger.setAttribute('aria-label', trigger.dataset.expandLabel ?? 'Expand')
      root.removeAttribute('data-collapse-expanded')

      options.forEach((opt) => {
        opt.setAttribute('tabindex', '-1')
      })
    }

    function toggle() {
      const expanded = trigger.getAttribute('aria-expanded') === 'true'
      expanded ? collapse() : expand()
    }

    if (isTouchDevice) {
      // touch device – by click
      trigger.addEventListener('click', (e) => {
        // если скрыт — не работаем
        const s = window.getComputedStyle(trigger)
        if (s.display === 'none' || s.visibility === 'hidden') return

        e.preventDefault()
        toggle()
      })

    } else {
      // mouse devices – by hover
      trigger.addEventListener('mouseenter', () => {
        const s = window.getComputedStyle(trigger)
        if (s.display === 'none' || s.visibility === 'hidden') return

        expand()
      })

      // keyboard devices – by keys
      trigger.addEventListener('keydown', (e) => {
        switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          // open and focus the 1st option
          expand(true)
          break

        case 'ArrowUp':
          e.preventDefault()
          // open and focus the last option
          expand(true)
          if (options.length) {
            focusOption(options.length - 1)
          }
          break

        case 'Enter':
        case ' ':
          e.preventDefault()
          toggle()
          if (trigger.getAttribute('aria-expanded') === 'true') focusOption(0)
          break

        case 'Escape':
          e.preventDefault()
          collapse()
          trigger.focus()
          break

        default:
          break
        }
      })

      //  hide when the mouse goes off from root
      const root = trigger.closest('[data-collapse-root]') || trigger

      root.addEventListener('mouseleave', () => {
        collapse()
      })
    }

    content.addEventListener('keydown', (e) => {
      if (!options.length) return

      switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        focusOption(activeIndex + 1)
        break

      case 'ArrowUp':
        e.preventDefault()
        focusOption(activeIndex - 1)
        break

      case 'Home':
        e.preventDefault()
        focusOption(0)
        break

      case 'End':
        e.preventDefault()
        focusOption(options.length - 1)
        break

      case 'Enter':
      case ' ':
        e.preventDefault()
        if (activeIndex >= 0) {
          const current = options[activeIndex]
          current.click()
          collapse()
          trigger.focus()
        }
        break

      case 'Escape':
        e.preventDefault()
        collapse()
        trigger.focus()
        break

      default:
        break
      }
    })
  }

  function resetCollapsible(root) {
    const trigger = root.querySelector('[data-collapse-trigger]')
    if (!trigger) return

    const targetId = trigger.dataset.collapseTarget
    if (!targetId) return

    const content = document.getElementById(targetId)
    if (!content) return

    if (content) {
      content.style.height = ''
      content.style.overflow = ''
      content.style.transition = ''
    }

    trigger.setAttribute('aria-label', trigger.dataset.expandLabel ?? 'Expand')

    trigger.removeAttribute('aria-expanded')
    trigger.removeAttribute('data-collapse-inited')
    root.removeAttribute('data-collapse-expanded')

    // remove listeners
    const clonedTrigger = trigger.cloneNode(true)
    const clonedContent = content.cloneNode(true)

    trigger.replaceWith(clonedTrigger)
    content.replaceWith(clonedContent)
  }

  const breakpoints = useBreakpoints()

  function parseCollapseInit(value) {
    // expected: tabletSm-down | tablet-sm-down | desktop-up | ...
    if (!value) return null

    const s = String(value).trim()
    const m = s.match(/^(.+)-(up|down)$/i)
    if (!m) {
      console.warn(`Collapse init rule "${s}" is invalid. Collapse init rule will be ignored.`)
      return null
    }

    return { name: m[1], dir: m[2].toLowerCase() }
  }

  function getMediaForRule(rule) {
    const bp = breakpoints.get(rule.name)
    if (!bp) {
      console.warn(`Breakpoint "${rule.name}" not found. Collapse init rule "${rule.name}-${rule.dir}" will be ignored.`)
      return null
    }

    if (rule.dir === 'down') {
      return window.matchMedia(`(max-width: ${bp - 1}px)`)
    }

    return window.matchMedia(`(min-width: ${bp}px)`)
  }


  document.addEventListener('DOMContentLoaded', init)
})()
